export interface ImageThumbnail {
    blob: Blob;
    width: number;
    height: number;
}

export const THUMBNAIL_MAX_EDGE_PX = 720;
export const THUMBNAIL_JPEG_QUALITY = 0.8;
export const THUMBNAIL_MIMETYPE = 'image/jpeg';

interface DecodedImage {
    source: CanvasImageSource;
    width: number;
    height: number;
    close: () => void;
}

async function decodeViaImageBitmap(file: File): Promise<DecodedImage> {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close(),
    };
}

async function decodeViaImageElement(file: File): Promise<DecodedImage> {
    const objectUrl = URL.createObjectURL(file);
    try {
        const img = new Image();
        img.src = objectUrl;
        if (typeof img.decode === 'function') {
            await img.decode();
        } else {
            await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error('Failed to decode image'));
            });
        }
        return {
            source: img,
            width: img.naturalWidth,
            height: img.naturalHeight,
            close: () => {},
        };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise(resolve => {
        canvas.toBlob(resolve, THUMBNAIL_MIMETYPE, THUMBNAIL_JPEG_QUALITY);
    });
}

export async function generateImageThumbnail(file: File): Promise<ImageThumbnail | null> {
    if (!file.type.startsWith('image/')) return null;

    try {
        const decoded = typeof createImageBitmap === 'function'
            ? await decodeViaImageBitmap(file).catch(() => decodeViaImageElement(file))
            : await decodeViaImageElement(file);

        try {
            const { width: sourceWidth, height: sourceHeight } = decoded;
            if (!sourceWidth || !sourceHeight) return null;

            const scale = Math.min(1, THUMBNAIL_MAX_EDGE_PX / Math.max(sourceWidth, sourceHeight));
            const width = Math.max(1, Math.round(sourceWidth * scale));
            const height = Math.max(1, Math.round(sourceHeight * scale));

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(decoded.source, 0, 0, width, height);

            const blob = await canvasToBlob(canvas);
            if (!blob) return null;

            return { blob, width, height };
        } finally {
            decoded.close();
        }
    } catch {
        return null;
    }
}
